import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const RiskAppetiteMonitoring = () => {
  // Mock risk appetite data
  const riskMetrics = [
    {
      id: 'METRIC-001',
      kpi: '% High-Risk Customers',
      policyLimit: '≤ 8%',
      currentResult: '6.3%',
      resultValue: 6.3,
      limitValue: 8.0,
      status: 'within',
      trend: 'stable',
      lastUpdated: '2024-11-27T10:00:00Z',
      history: [5.8, 6.1, 6.3, 6.2, 6.3],
      description: 'Percentage of customer base classified as high-risk',
      breachHistory: []
    },
    {
      id: 'METRIC-002',
      kpi: 'Volume from High-Risk Corridors',
      policyLimit: '≤ 12%',
      currentResult: '9.9%',
      resultValue: 9.9,
      limitValue: 12.0,
      status: 'within',
      trend: 'increasing',
      lastUpdated: '2024-11-27T10:00:00Z',
      history: [8.5, 9.1, 9.4, 9.7, 9.9],
      description: 'Transaction volume from designated high-risk geographic corridors',
      breachHistory: []
    },
    {
      id: 'METRIC-003',
      kpi: 'Average Transaction Risk Score',
      policyLimit: '≤ 65',
      currentResult: '58.2',
      resultValue: 58.2,
      limitValue: 65.0,
      status: 'within',
      trend: 'stable',
      lastUpdated: '2024-11-27T10:00:00Z',
      history: [57.3, 58.1, 57.9, 58.5, 58.2],
      description: 'Mean risk score across all transactions',
      breachHistory: []
    },
    {
      id: 'METRIC-004',
      kpi: 'STR/SAR Filing Rate',
      policyLimit: '≤ 2.5%',
      currentResult: '1.8%',
      resultValue: 1.8,
      limitValue: 2.5,
      status: 'within',
      trend: 'decreasing',
      lastUpdated: '2024-11-27T10:00:00Z',
      history: [2.1, 1.9, 1.9, 1.8, 1.8],
      description: 'Percentage of transactions requiring STR/SAR filing',
      breachHistory: []
    },
    {
      id: 'METRIC-005',
      kpi: 'Enhanced Due Diligence Rate',
      policyLimit: '≤ 15%',
      currentResult: '12.4%',
      resultValue: 12.4,
      limitValue: 15.0,
      status: 'within',
      trend: 'increasing',
      lastUpdated: '2024-11-27T10:00:00Z',
      history: [10.8, 11.2, 11.9, 12.1, 12.4],
      description: 'Transactions requiring enhanced due diligence',
      breachHistory: []
    },
    {
      id: 'METRIC-006',
      kpi: 'PEP Customer Concentration',
      policyLimit: '≤ 5%',
      currentResult: '3.7%',
      resultValue: 3.7,
      limitValue: 5.0,
      status: 'within',
      trend: 'stable',
      lastUpdated: '2024-11-27T10:00:00Z',
      history: [3.5, 3.6, 3.8, 3.7, 3.7],
      description: 'Percentage of politically exposed person accounts',
      breachHistory: []
    }
  ];

  const getStatusBadge = (status) => {
    const config = {
      within: { bg: 'bg-success/10', text: 'text-success', icon: 'CheckCircle', label: 'Within Limit' },
      warning: { bg: 'bg-warning/10', text: 'text-warning', icon: 'AlertTriangle', label: 'Approaching Limit' },
      breach: { bg: 'bg-error/10', text: 'text-error', icon: 'AlertCircle', label: 'Limit Breached' }
    };
    const badge = config?.[status] || config?.within;
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${badge?.bg} ${badge?.text}`}>
        <Icon name={badge?.icon} size={14} className="mr-1" />
        {badge?.label}
      </span>
    );
  };

  const getTrendIcon = (trend) => {
    const icons = {
      increasing: { name: 'TrendingUp', color: 'text-warning' },
      decreasing: { name: 'TrendingDown', color: 'text-success' },
      stable: { name: 'Minus', color: 'text-muted-foreground' }
    };
    const icon = icons?.[trend] || icons?.stable;
    return <Icon name={icon?.name} size={16} className={icon?.color} />;
  };

  const getProgressPercentage = (result, limit) => {
    return Math.min((result / limit) * 100, 100);
  };

  const getProgressColor = (percentage) => {
    if (percentage >= 90) return 'bg-error';
    if (percentage >= 75) return 'bg-warning';
    return 'bg-success';
  };

  // Calculate overall compliance
  const withinLimits = riskMetrics?.filter(m => m?.status === 'within')?.length;
  const complianceRate = ((withinLimits / riskMetrics?.length) * 100)?.toFixed(1);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-foreground">Risk Appetite Statement Monitoring</h2>
            <p className="text-muted-foreground mt-1">
              Track compliance with institutional risk appetite and policy limits
            </p>
          </div>
          <button className="flex items-center space-x-2 px-4 py-2 border border-border rounded-lg hover:bg-muted/50 transition-colors">
            <Icon name="Settings" size={20} />
            <span>Configure Limits</span>
          </button>
        </div>

        {/* Overall Compliance */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-muted/30 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Overall Compliance</p>
                <p className="text-3xl font-bold text-success mt-1">{complianceRate}%</p>
              </div>
              <div className="w-16 h-16 rounded-full bg-success/10 flex items-center justify-center">
                <Icon name="Target" size={32} className="text-success" />
              </div>
            </div>
            <div className="mt-3 text-xs text-muted-foreground">
              {withinLimits} of {riskMetrics?.length} metrics within policy limits
            </div>
          </div>
          <div className="bg-muted/30 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Active Metrics</p>
                <p className="text-3xl font-bold text-foreground mt-1">{riskMetrics?.length}</p>
              </div>
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                <Icon name="Activity" size={32} className="text-primary" />
              </div>
            </div>
            <div className="mt-3 text-xs text-muted-foreground">
              Monitored KPIs across all risk categories
            </div>
          </div>
          <div className="bg-muted/30 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Policy Breaches</p>
                <p className="text-3xl font-bold text-success mt-1">0</p>
              </div>
              <div className="w-16 h-16 rounded-full bg-success/10 flex items-center justify-center">
                <Icon name="ShieldCheck" size={32} className="text-success" />
              </div>
            </div>
            <div className="mt-3 text-xs text-muted-foreground">
              No limit breaches in current period
            </div>
          </div>
        </div>
      </div>
      {/* Risk Metrics Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {riskMetrics?.map((metric) => {
          const progressPercentage = getProgressPercentage(metric?.resultValue, metric?.limitValue);
          const progressColor = getProgressColor(progressPercentage);
          
          return (
            <div key={metric?.id} className="bg-card border border-border rounded-lg p-6">
              {/* Metric Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-foreground mb-1">{metric?.kpi}</h3>
                  <p className="text-sm text-muted-foreground">{metric?.description}</p>
                </div>
                <div className="flex items-center space-x-2">
                  {getTrendIcon(metric?.trend)}
                  {getStatusBadge(metric?.status)}
                </div>
              </div>
              {/* Current vs Limit */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="bg-muted/30 p-3 rounded-lg">
                  <p className="text-xs text-muted-foreground mb-1">Current Result</p>
                  <p className="text-2xl font-bold text-foreground">{metric?.currentResult}</p>
                </div>
                <div className="bg-muted/30 p-3 rounded-lg">
                  <p className="text-xs text-muted-foreground mb-1">Policy Limit</p>
                  <p className="text-2xl font-bold text-muted-foreground">{metric?.policyLimit}</p>
                </div>
              </div>
              {/* Progress Bar */}
              <div className="mb-4">
                <div className="flex items-center justify-between text-xs text-muted-foreground mb-2">
                  <span>Utilization: {progressPercentage?.toFixed(1)}%</span>
                  <span>Headroom: {(100 - progressPercentage)?.toFixed(1)}%</span>
                </div>
                <div className="w-full bg-muted/30 rounded-full h-3 overflow-hidden">
                  <div
                    className={`h-full ${progressColor} transition-all duration-300 rounded-full`}
                    style={{ width: `${progressPercentage}%` }}
                  ></div>
                </div>
              </div>
              {/* Mini Chart */}
              <div className="mb-3">
                <p className="text-xs text-muted-foreground mb-2">5-Period Trend</p>
                <div className="flex items-end justify-between h-16 space-x-1">
                  {metric?.history?.map((value, idx) => {
                    const height = (value / metric?.limitValue) * 100;
                    return (
                      <div key={idx} className="flex-1 flex flex-col items-center justify-end">
                        <div
                          className={`w-full ${progressColor} rounded-t`}
                          style={{ height: `${height}%` }}
                        ></div>
                      </div>
                    );
                  })}
                </div>
              </div>
              {/* Last Updated */}
              <div className="text-xs text-muted-foreground pt-3 border-t border-border">
                Last updated: {new Date(metric?.lastUpdated)?.toLocaleString()}
              </div>
            </div>
          );
        })}
      </div>
      {/* Governance Statement */}
      <div className="bg-primary/5 border border-primary/20 rounded-lg p-6">
        <div className="flex items-start space-x-4">
          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Icon name="ShieldCheck" size={24} className="text-primary" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-foreground mb-2">Regulatory Compliance Statement</h3>
            <p className="text-sm text-muted-foreground mb-4">
              This Risk Appetite Statement Monitoring demonstrates our organization's adherence to established 
              risk management policies and regulatory requirements. All metrics are within approved tolerance 
              levels, evidencing effective risk governance and control frameworks.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div className="flex items-center space-x-2">
                <Icon name="CheckCircle" size={16} className="text-success" />
                <span className="text-foreground">Policy Compliance: 100%</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="CheckCircle" size={16} className="text-success" />
                <span className="text-foreground">Monitoring Frequency: Real-time</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="CheckCircle" size={16} className="text-success" />
                <span className="text-foreground">Board Review: Monthly</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RiskAppetiteMonitoring;